import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2 } from "lucide-react";
import { ContactInfo } from "./contact/ContactInfo";
import { useContactForm, classOptions, dayOptions, heardFromOptions, childrenOptions, isReferral } from "./contact/useContactForm";

const inputCls =
  "w-full bg-white/10 border border-white/15 rounded-2xl px-5 py-3.5 text-white placeholder:text-white/50 focus:outline-none focus:border-[#FF7A1A] focus:bg-white/15 transition-all";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-[#94A3B8] mb-2.5";
const hintCls = "text-white/40 normal-case font-medium tracking-normal";
const chip = (active) =>
  `px-4 py-2 rounded-full text-sm font-semibold border transition-all duration-200 ${
    active ? "bg-[#FF7A1A] text-white border-transparent shadow-sm" : "bg-white/10 text-white/80 border-white/15 hover:border-white/40"
  }`;
const slug = (s) => s.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();

const Reveal = ({ show, children, ...rest }) => (
  <AnimatePresence initial={false}>
    {show && (
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
        {...rest}
      >
        {children}
      </motion.div>
    )}
  </AnimatePresence>
);

function ChipGroup({ options, selected, onToggle, groupId, itemId }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={groupId}>
      {options.map((o) => (
        <button type="button" key={o} onClick={() => onToggle(o)} className={chip(selected.includes(o))} data-testid={`${itemId}-${slug(o)}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

function ContactForm() {
  const { form, loading, update, toggleIn, submit } = useContactForm();
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
      {/* Honeypot: hidden from humans, bots tend to fill it */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
        <label htmlFor="contact-website">Website</label>
        <input id="contact-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={update("website")} data-testid="contact-honeypot" />
      </div>
      <input required value={form.name} onChange={update("name")} placeholder="Full name" className={inputCls} data-testid="contact-name" />
      <input required type="email" value={form.email} onChange={update("email")} placeholder="Email address" className={inputCls} data-testid="contact-email" />
      <input value={form.phone} onChange={update("phone")} placeholder="Phone (optional)" className={inputCls} data-testid="contact-phone" />

      <div>
        <label className={labelCls}>How many children?</label>
        <select value={form.children} onChange={update("children")} className={`${inputCls} appearance-none`} data-testid="contact-children">
          <option value="" className="text-[#0A2540]">Select number of children</option>
          {childrenOptions.map(([v, label]) => <option key={v} value={v} className="text-[#0A2540]">{label}</option>)}
        </select>
      </div>

      <Reveal show={!!form.children} data-testid="contact-dynamic">
        <div className="space-y-5">
          <div className="pt-1">
            <label className={labelCls}>Which classes interest you? <span className={hintCls}>(select all that apply)</span></label>
            <ChipGroup options={classOptions} selected={form.classes} onToggle={(c) => toggleIn("classes", c)} groupId="contact-classes" itemId="contact-class" />
          </div>
          <div>
            <label className={labelCls}>Preferred days <span className={hintCls}>(optional)</span></label>
            <ChipGroup options={dayOptions} selected={form.days} onToggle={(d) => toggleIn("days", d)} groupId="contact-days" itemId="contact-day" />
          </div>
        </div>
      </Reveal>

      <div>
        <label className={labelCls}>How did you find us?</label>
        <select value={form.heardFrom} onChange={update("heardFrom")} className={`${inputCls} appearance-none`} data-testid="contact-heard-from">
          <option value="" className="text-[#0A2540]">Please choose…</option>
          {heardFromOptions.map((h) => <option key={h} value={h} className="text-[#0A2540]">{h}</option>)}
        </select>
      </div>

      <Reveal show={isReferral(form.heardFrom)}>
        <label className={labelCls}>Who recommended us? <span className={hintCls}>(swimmer's name, so we can say thank you)</span></label>
        <input value={form.referredBy} onChange={update("referredBy")} placeholder="e.g. Lily Thompson" maxLength={120} className={inputCls} data-testid="contact-referred-by" />
      </Reveal>

      <textarea required value={form.message} onChange={update("message")} placeholder="Tell us about your swimmer, their age and any questions..." rows={4} className={`${inputCls} resize-none`} data-testid="contact-message" />
      <button
        type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 bg-[#FF7A1A] text-white font-bold px-8 py-4 rounded-full shadow-[0_8px_20px_-6px_rgba(255,122,26,0.6)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60"
        data-testid="contact-submit"
      >
        {loading ? <Loader2 className="animate-spin" size={20} /> : <Send size={18} />}
        {loading ? "Sending..." : "Send enquiry"}
      </button>
    </form>
  );
}

export default function Contact() {
  return (
    <section id="contact" className="py-24 md:py-32" data-testid="contact-section">
      <div className="container mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="bg-[#0A2540] rounded-[2.5rem] p-8 md:p-16 shadow-2xl relative overflow-hidden"
        >
          <div className="blob absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#FF7A1A]/20" />
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 relative">
            <ContactInfo />
            <ContactForm />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
